// src/pages/public/Checkout.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ClipboardDocumentIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiClient from '../../api/client';
import { useAuth } from '../../context/AuthContext';

const plansData = {
  1: { id: 1, name: 'Starter', price: 250, billing: 'per term', features: ['Up to 500 Students', 'Up to 20 Teachers', 'Student Management', 'Teacher Management'] },
  2: { id: 2, name: 'Basic', price: 500, billing: 'per term', features: ['Up to 750 Students', 'Up to 30 Teachers', 'Attendance Tracking', 'Everything in Starter'] },
  3: { id: 3, name: 'Pro', price: 1000, billing: 'per term', features: ['Up to 1,500 Students', 'Up to 100 Teachers', 'Advanced Reports', 'Everything in Basic'] },
  4: { id: 4, name: 'Enterprise', price: 2000, billing: 'per term', features: ['Unlimited Students', 'Unlimited Teachers', 'Dedicated Support', 'Everything in Pro'] },
};

const money = (value) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authReady, loading: authLoading, isAuthenticated, refreshUser } = useAuth();
  const [payment, setPayment] = useState(null);
  const [starting, setStarting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [restoring, setRestoring] = useState(true);
  const completedRef = useRef(false);

  const selectedPlan = useMemo(() => {
    const id = new URLSearchParams(location.search).get('planId');
    return plansData[id] || null;
  }, [location.search]);

  const plan = payment?.plan
    ? { ...selectedPlan, ...payment.plan, price: payment.amount || selectedPlan?.price }
    : selectedPlan;

  useEffect(() => {
    if (!authReady || authLoading) return;
    if (!isAuthenticated) {
      const returnUrl = location.pathname + location.search;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`, { replace: true });
    } else if (!selectedPlan) {
      toast.error('Invalid plan selected');
      navigate('/subscribe_to_plan', { replace: true });
    }
  }, [authReady, authLoading, isAuthenticated, selectedPlan, location, navigate]);

  const finish = async (message) => {
    if (completedRef.current) return;
    completedRef.current = true;
    await refreshUser();
    localStorage.removeItem('selectedPlan');
    toast.success(message || `${plan?.name || 'Your'} plan is active 🎉`);
    navigate('/app/dashboard', { replace: true });
  };

  useEffect(() => {
    if (!authReady || authLoading || !isAuthenticated || !selectedPlan) return;
    let active = true;
    const restore = async () => {
      try {
        const response = await apiClient.get('/subscriptions/payment/pending');
        if (!active) return;
        if (response.data?.paid) return finish('Plan payment confirmed and subscription activated');
        const pending = response.data?.data;
        if (pending && Number(pending.plan?.id) === Number(selectedPlan.id)) setPayment(pending);
      } catch (error) {
        console.error('Unable to restore plan payment:', error);
      } finally {
        if (active) setRestoring(false);
      }
    };
    restore();
    return () => { active = false; };
    // finish intentionally excluded: navigation happens only after a paid result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, authLoading, isAuthenticated, selectedPlan]);

  const startPayment = async () => {
    if (!selectedPlan || starting) return;
    setStarting(true);
    try {
      const response = await apiClient.post('/subscriptions/payment/initiate', { planId: selectedPlan.id });
      if (response.data?.paid) return finish(response.data?.message);
      setPayment(response.data?.data || null);
      toast.success('Plan Upgrade payment code created');
    } catch (error) {
      if (error.response?.data?.paid) return finish(error.response.data.message);
      toast.error(error.response?.data?.message || 'Unable to create the payment code');
    } finally {
      setStarting(false);
    }
  };

  const confirmPayment = async (silent = false) => {
    if (!payment?.paymentId || confirming || completedRef.current) return;
    setConfirming(true);
    try {
      const response = await apiClient.post('/subscriptions/payment/confirm', { payment_id: payment.paymentId });
      if (response.data?.paid) return finish(response.data?.message);
      if (!silent) toast('Payment is still pending. Complete the mobile-money prompt and try again.', { icon: '⏳' });
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.message || 'Payment confirmation failed');
      if (['expired', 'cancelled', 'failed'].includes(error.response?.data?.status)) setPayment(null);
    } finally {
      setConfirming(false);
    }
  };

  useEffect(() => {
    if (!payment?.paymentId || completedRef.current) return undefined;
    const timer = setInterval(() => confirmPayment(true), 7000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment?.paymentId]);

  const copyCode = async () => {
    if (!payment?.ussdCode) return;
    try { await navigator.clipboard.writeText(payment.ussdCode); toast.success('USSD code copied'); }
    catch { toast.error('Could not copy the code'); }
  };

  if (!authReady || authLoading || restoring || !plan) {
    return <div className="fixed inset-0 flex items-center justify-center bg-white"><div className="text-center"><div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-primary-600" /><p className="mt-4 text-slate-600">Loading secure checkout...</p></div></div>;
  }

  return (
    <div className="fixed inset-0 z-[200] overflow-auto bg-slate-50">
      <button type="button" onClick={() => navigate('/subscribe_to_plan', { replace: true })} disabled={starting || confirming} className="fixed right-6 top-6 z-50 rounded-full border border-slate-200 bg-white p-3 shadow-sm hover:bg-slate-100 disabled:opacity-50" aria-label="Return to plans"><XMarkIcon className="h-7 w-7 text-slate-600" /></button>
      <div className="mx-auto max-w-6xl px-6 pb-16 pt-20">
        <div className="mb-12 text-center"><span className="inline-flex rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">Secure Monime checkout</span><h1 className="mt-5 text-4xl font-bold text-slate-800">Plan Upgrade</h1><p className="mt-3 text-lg text-slate-600">Pay for the <span className="font-semibold text-primary-600">{plan.name} Plan</span> for the current academic term.</p></div>
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="mb-6 text-2xl font-semibold text-slate-800">What&apos;s included</h2>
            <ul className="space-y-4">{(plan.features || selectedPlan.features).map((feature) => <li key={feature} className="flex items-start gap-3"><CheckCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-primary-500" /><span className="text-slate-700">{feature}</span></li>)}</ul>
            <div className="mt-10 border-t border-slate-200 pt-6"><div className="flex items-end justify-between"><span className="text-slate-500">Total amount</span><div><span className="text-4xl font-bold text-primary-600">{money(plan.price)}</span><span className="ml-1 text-slate-400">SLE</span></div></div><p className="mt-1 text-sm text-slate-500">Billed per academic term</p>{payment?.term?.name && <p className="mt-3 rounded-xl bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-700">Payment for: {payment.term.name}</p>}</div>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-semibold text-slate-800">Pay with Mobile Money</h2>
            {!payment ? (
              <><p className="mb-8 mt-2 text-slate-600">Generate a secure one-time Monime code for this Plan Upgrade.</p><button type="button" onClick={startPayment} disabled={starting} className="mt-8 w-full rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 py-5 text-lg font-semibold text-white disabled:opacity-60">{starting ? 'Creating secure code...' : 'Generate Payment Code'}</button></>
            ) : (
              <><p className="mb-6 mt-2 text-slate-600">Dial the code below and approve the exact amount shown on your phone.</p><div className="mb-6 rounded-2xl border border-primary-200 bg-primary-50 p-7 text-center"><p className="text-xs font-bold uppercase tracking-wider text-primary-500">Dial this code</p><button type="button" onClick={copyCode} className="mt-3 inline-flex items-center gap-3 text-3xl font-bold text-primary-700"><span className="font-mono">{payment.ussdCode || 'Preparing code...'}</span><ClipboardDocumentIcon className="h-6 w-6" /></button><p className="mt-4 text-sm font-medium text-slate-600">Reference: Plan Upgrade</p></div><div className="flex items-center gap-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-700"><ClockIcon className="h-5 w-5 shrink-0" />Waiting for Monime to confirm your payment. This page checks automatically.</div><button type="button" onClick={() => confirmPayment(false)} disabled={confirming} className="mt-6 w-full rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 py-5 text-lg font-semibold text-white disabled:opacity-60">{confirming ? 'Checking Payment...' : 'I Have Paid — Check Now'}</button></>
            )}
            <p className="mt-6 text-center text-xs text-slate-400">Your subscription activates only after Monime verifies the amount, school, plan and academic term.</p>
          </div>
        </div>
      </div>
    </div>
  );
}